import { useEffect, useState } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'
import spacetime from 'spacetime'

import { getTimelineActivities } from 'api/AccountActivityAPI'
import { PagedActivityTimeline } from 'api/AccountActivityAPI/types'
import { DateRange } from 'components/DateRangePicker'
import { ActivityEndUser, ActivityEvent, ActivityType } from 'modules/accountActivity/domain'
import {
    emailBundle,
    getEmailDataFromActivity,
    meetingsBundle,
    pageViewBundle
} from 'utils/activityTimeline'

export const useActivityTimeline = (
    useTwoWeekPreview: boolean,
    dateRange?: DateRange,
    accountId?: string,
    contactId?: string
) => {
    const [endUserData, setEndUserData] = useState<Record<number, ActivityEndUser>>({})
    const [activityData, setActivityData] = useState<Record<number, ActivityEvent[]>>({})
    const [dataTotal, setDataTotal] = useState(0)
    const limit = 25

    const { fetchNextPage, hasNextPage, data, isLoading, error } = useInfiniteQuery({
        queryKey: [`getTimelineActivities`, accountId ?? contactId],
        queryFn: ({ pageParam = Date.now() }) =>
            getTimelineActivities(pageParam, limit, accountId, contactId),
        getNextPageParam: (lastPage: PagedActivityTimeline) =>
            lastPage.nextPagePath != null ? lastPage.nextPageStartTimestamp : undefined,
    });

    useEffect(() => {
        const activityTimeline = data?.pages.flatMap(page => page.data) ?? []
        const combinedPages = activityTimeline?.flatMap(timeline => timeline.activityEvents) ?? []
        const endUserMap = activityTimeline.length > 0 && activityTimeline[0].endUsers
            ? activityTimeline[0].endUsers.reduce((map, activityEndUser) => {
                map[activityEndUser.id] = activityEndUser;
                return map;
            }, {} as Record<number, ActivityEndUser>)
            : {};
        const bundledEvents = combinedPages.reduce(
            (acc: ActivityEvent[], activity: ActivityEvent, index: number, startArray: ActivityEvent[]) => {
                switch (activity.activityType) {
                    case ActivityType.PAGE_VIEW:
                        acc = pageViewBundle(limit, activity, startArray[index - 1], acc)
                        break
                    case ActivityType.MEETING_BOOKED:
                        acc = meetingsBundle(endUserMap[activity.endUserId], activity, startArray[index - 1], acc)
                        break
                    case ActivityType.EMAIL_CLICK:
                    case ActivityType.EMAIL_CLICK_SFDC:
                    case ActivityType.EMAIL_OPEN:
                    case ActivityType.EMAIL_OPEN_SFDC:
                        acc = emailBundle(activity, acc)
                        break
                    case ActivityType.EMAIL_INBOUND:
                        acc.push({
                            ...activity,
                            activityContext: {
                                ...activity.activityContext,
                                emailPreviewData: getEmailDataFromActivity(activity)
                            }
                        })
                        break
                    default:
                        acc.push(activity)
                        break
                }
                return acc
            },
            []
        )

        //group activities by day
        const groupedData = bundledEvents.reduce(
            (acc: Record<number, ActivityEvent[]>, activity: ActivityEvent) => {
                const date = spacetime(activity.createdAt).startOf('day').epoch
                if (!acc[date]) {
                    acc[date] = []
                }
                acc[date].push(activity)
                return acc
            },
            {}
        )
        setEndUserData(endUserMap)
        setActivityData(groupedData)
        setDataTotal(bundledEvents?.length ?? 0)
    }, [data])

    return {
        endUserData,
        data: activityData,
        dataTotal,
        error,
        isLoading,
        hasMoreActivity: hasNextPage,
        fetchNextPage
    }
}